import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Header from './components/Header';
import Home from './pages/Home';
import About from './pages/About';
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import AccountInactive from './pages/AccountInactive';
import LoadingSpinner from './components/LoadingSpinner';
import TestUploader from './pages/TestUploader';
import ModifyPDFPage from './pages/ModifyPDFPage';
import CreateFieldExperience from './pages/CreateFieldExperience';
import MyEventsRoute from './components/MyEventsRoute';
import GuestPresentEntry from './pages/GuestPresentEntry';
import SAFForm from './pages/SAFForm';
import TestTeamUpEvent from './pages/TestTeamUpEvent';
import TeamUpCalendarManager from './pages/TeamUpCalendarManager';
import FEeventRegistration from './pages/FEeventRegistration';
import FEeventRegistrationsManager from './pages/FEeventRegistrationsManager';
import FEeventAttendance from './pages/FEeventAttendance';
import MyHours from './pages/MyHours';
import AssignmentBuilder from './pages/AssignmentBuilder';
import AssignmentViewPage from './pages/AssignmentViewPage';
import MyDirectory from './pages/MyDirectory';
import AdminDirectory from './pages/AdminDirectory';
import NullHoursSubmissions from './pages/NullHoursSubmissions';
import AssignmentSubmissionsAuditor from './pages/AssignmentSubmissionsAuditor';
import ProjectBuilder from './pages/ProjectBuilder';
import ProjectsDirectory from './pages/ProjectsDirectory';
import RequestFEHoursByExternalActivity from './pages/RequestFEHoursByExternalActivity';
import WaiverForms from './pages/WaiverForms';
import EmailTest from './pages/EmailTest';
import EmailTemplateGenerator from './pages/EmailTemplateGenerator';
import UserManagement from './pages/UserManagement';
import ScheduleBuilder from './pages/ScheduleBuilder';
import UserAccountUpdate from './pages/UserAccountUpdate';
import RelationshipsManager from './pages/RelationshipsManager';
import Newsletter from './pages/Newsletter';
import NewsletterViewer from './pages/NewsletterViewer';
import OpenFEEventSurvey from './pages/OpenFEEventSurvey';
import ActionLinkPage from './pages/ActionLink';
import OpenRegistrationMatcher from './pages/OpenRegistrationMatcher';
import CourseBuilder from './pages/CourseBuilder';
import CourseViewer from './pages/CourseViewer';
import CourseEnrollManager from './pages/CourseEnrollManager';
import MyCourses from './pages/MyCourses';
import MyCoursesManager from './pages/MyCoursesManager';
import ComponentSubmissions from './pages/ComponentSubmissions';
import SMSNotifications from './pages/SMSNotifications';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Taxonomy from './pages/Taxonomy';
import CSVImport from './pages/CSVImport';
import Community from './pages/Community';
import Analytics from './pages/Analytics';
import AdminCourses from './pages/AdminCourses';
import StudentHoursOverview from './pages/StudentHoursOverview';
import PageAccessManager from './pages/PageAccessManager';

const PrivateRoute = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [requiresAuth, setRequiresAuth] = useState(false);
  const [authError, setAuthError] = useState(false);
  const location = useLocation();
  const token = localStorage.getItem('token');

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setAuthError(false);

    const checkAccess = async () => {
      try {
        const response = await axios.get(`/api/check-permission?pagePath=${location.pathname.slice(1)}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        
        if (mounted) {
          setHasAccess(response.data.hasAccess);
          setRequiresAuth(response.data.requiresAuth || false);
          setLoading(false);
        }
      } catch (error) {
        console.error('Error checking permission:', error);
        if (mounted) {
          // If it's an authentication error (401), clear token and set auth error
          if (error.response?.status === 401) {
            localStorage.removeItem('token');
            setAuthError(true);
          }
          setHasAccess(false);
          setLoading(false);
        }
      }
    };

    checkAccess();

    return () => {
      mounted = false;
    };
  }, [location.pathname, location.search, token]);

  // Handle authentication error by redirecting to login
  if (authError) {
    const returnTo = location.pathname + location.search + location.hash;
    return <Navigate to="/login" state={{ from: returnTo }} replace />;
  }

  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.8)', // Light overlay to match app's aesthetic
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999
      }}>
        <LoadingSpinner />
      </div>
    );
  }

  if (requiresAuth && !token) {
    const returnTo = location.pathname + location.search + location.hash;
    return <Navigate to="/login" state={{ from: returnTo }} replace />;
  }

  if (!hasAccess) {
    return (
      <div style={styles.accessDenied}>
        <h1 style={styles.accessDeniedTitle}>Access Denied</h1>
        <p style={styles.accessDeniedText}>You do not have permission to access this page.</p>
      </div>
    );
  }

  return children;
};

function App() {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [navQuickLinks, setNavQuickLinks] = useState([]);
  const [navQuickLinksReady, setNavQuickLinksReady] = useState(true);

  // Global axios interceptor to handle 401 errors (expired tokens)
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Token expired or invalid - clear it and redirect to login
          localStorage.removeItem('token');
          setUserData(null);
          // Only redirect if we're not already on login page
          if (window.location.pathname !== '/login') {
            // Preserve the full URL including path, search params, and hash
            const returnTo = window.location.pathname + window.location.search + window.location.hash;
            window.location.href = `/login?returnTo=${encodeURIComponent(returnTo)}`;
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    const fetchUserData = async () => {
      const token = localStorage.getItem('token');
      
      if (token) {
        try {
          const response = await axios.get('/api/user', {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (mounted) {
            setUserData(response.data);
            
            // Track app session (user returning with valid token)
            try {
              await axios.post('/api/track-session', {
                sessionType: 'app_session',
                userAgent: navigator.userAgent,
                timestamp: new Date().toISOString()
              }, {
                headers: { Authorization: `Bearer ${token}` }
              });
            } catch (trackingError) {
              console.warn('Error tracking app session:', trackingError);
              // Don't fail app load if tracking fails
            }
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          localStorage.removeItem('token');
          if (mounted) {
            setUserData(null);
          }
        }
      } else {
        // No token, clear userData
        if (mounted) {
          setUserData(null);
        }
      }
      if (mounted) {
        setLoading(false);
      }
    };

    fetchUserData();

    return () => {
      mounted = false;
    };
  }, [refreshTrigger]);

  useEffect(() => {
    if (!userData) {
      setNavQuickLinks([]);
      setNavQuickLinksReady(true);
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) {
      setNavQuickLinks([]);
      setNavQuickLinksReady(true);
      return;
    }

    let cancelled = false;
    setNavQuickLinksReady(false);

    (async () => {
      try {
        const res = await axios.get('/api/home-widgets', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        // Source of truth is the `home.widgets` permission row(s).
        // No row / unmatched audience -> empty menu, by design.
        setNavQuickLinks(Array.isArray(res.data.widgets) ? res.data.widgets : []);
      } catch (err) {
        console.error('Nav quick links fetch failed:', err);
        if (!cancelled) {
          setNavQuickLinks([]);
        }
      } finally {
        if (!cancelled) {
          setNavQuickLinksReady(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userData]);


  if (loading) {
    return <LoadingSpinner />;
  }

  return (

      <Router>
        <div className="App">
          <Header
            userData={userData}
            setUserData={setUserData}
            navQuickLinks={navQuickLinks}
            navQuickLinksReady={navQuickLinksReady}
          />
          <Routes>
          <Route path="/login" element={<Login setUserData={setUserData} userData={userData} />} />
          <Route path="/guest-present" element={<GuestPresentEntry />} />
          <Route path="/account-inactive" element={<AccountInactive />} />
          <Route path="/" element={
            <PrivateRoute>
              <Home
                userData={userData}
                navQuickLinks={navQuickLinks}
                navQuickLinksReady={navQuickLinksReady}
              />
            </PrivateRoute>
          } />
          <Route path="/about" element={
            <PrivateRoute>
              <About userData={userData} />
            </PrivateRoute>
          } />
          <Route path="/dashboard" element={
            <PrivateRoute>
              <Dashboard userData={userData} setUserData={setUserData} />
            </PrivateRoute>
          } />
          <Route path="/update-account" element={
            <PrivateRoute>
              <UserAccountUpdate userData={userData} setUserData={setUserData} />
            </PrivateRoute>
          } />
          <Route path="/test-uploader" element={
            <PrivateRoute>
              <TestUploader userData={userData} />
            </PrivateRoute>
          } />
          <Route path="/modify-pdf" element={
            <PrivateRoute>
              <ModifyPDFPage userData={userData} />
            </PrivateRoute>
          } />
          <Route path="/create-field-experience" element={
            <PrivateRoute>
              <CreateFieldExperience userData={userData} />
            </PrivateRoute>
          } />
          <Route path="/my-events" element={
            <MyEventsRoute userData={userData} setUserData={setUserData} PrivateRoute={PrivateRoute} />
          } />
          <Route path="/saf-form/:eventId" element={
            <PrivateRoute>
              <SAFForm userData={userData} />
            </PrivateRoute>
          } />
          <Route path="/register-for-field-experience" element={
            <PrivateRoute>
              <FEeventRegistration userData={userData} />
            </PrivateRoute>
          } />
          <Route path="/register-a-student-for-an-event" element={
            <PrivateRoute>
              <FEeventRegistrationsManager userData={userData} />
            </PrivateRoute>
          } />
          <Route path="/fe-event-attendance" element={
            <PrivateRoute>
              <FEeventAttendance userData={userData} />
            </PrivateRoute>
          } />
          <Route path="/my-hours" element={
            <PrivateRoute>
              <MyHours userData={userData} />
            </PrivateRoute>
          } />
          <Route path="/assignment-builder" element={
            <PrivateRoute>
              <AssignmentBuilder userData={userData} />
            </PrivateRoute>
          } />
          <Route path="/my-directory" element={
            <PrivateRoute>
              <MyDirectory userData={userData} />
            </PrivateRoute>
          } />
          <Route path="/admin-directory" element={
            <PrivateRoute>
              <AdminDirectory userData={userData} />
            </PrivateRoute>
          } />
          <Route path="/null-hours-submissions" element={
            <PrivateRoute>
              <NullHoursSubmissions userData={userData} />
            </PrivateRoute>
          } />
          <Route path="/assignment-submissions-auditor" element={
            <PrivateRoute>
              <AssignmentSubmissionsAuditor userData={userData} />
            </PrivateRoute>
          } />
          <Route path="/project-builder" element={
            <PrivateRoute>
              <ProjectBuilder userData={userData} />
            </PrivateRoute>
          } />
          <Route path="/projects-directory" element={
            <PrivateRoute>
              <ProjectsDirectory userData={userData} />
            </PrivateRoute>
          } />
          <Route path="/request-fe-hours-by-external-activity" element={
            <PrivateRoute>
              <RequestFEHoursByExternalActivity userData={userData} setUserData={setUserData} />
            </PrivateRoute>
          } />
          <Route path="/waiver-forms" element={
            <PrivateRoute>
              <WaiverForms userData={userData} />
            </PrivateRoute>
          } />
          <Route path="/schedule-builder" element={
            <PrivateRoute>
              <ScheduleBuilder userData={userData} />
            </PrivateRoute>
          } />
          <Route path="/user-management" element={
            <PrivateRoute>
              <UserManagement userData={userData} />
            </PrivateRoute>
          } />
          <Route path="/page-access-manager" element={
            <PrivateRoute>
              <PageAccessManager />
            </PrivateRoute>
          } />
          <Route path="/relationships" element={
            <PrivateRoute>
              <RelationshipsManager userData={userData} />
            </PrivateRoute>
          } />
          {/* Legacy redirect — preserves existing bookmarks/links */}
          <Route path="/instructor-concentrations" element={<Navigate to="/relationships" replace />} />
          <Route path="/newsletter" element={
            <PrivateRoute>
              <Newsletter userData={userData} />
            </PrivateRoute>
          } />
          <Route path="/newsletter-viewer" element={
            <PrivateRoute>
              <NewsletterViewer userData={userData} />
            </PrivateRoute>
          } />
          <Route path="/course-builder" element={
            <PrivateRoute>
              <CourseBuilder userData={userData} />
            </PrivateRoute>
          } />
          <Route path="/course-viewer" element={
            <PrivateRoute>
              <CourseViewer userData={userData} />
            </PrivateRoute>
          } />
          <Route path="/course-enroll-manager" element={
            <PrivateRoute>
              <CourseEnrollManager userData={userData} />
            </PrivateRoute>
          } />
          <Route path="/my-courses" element={
            <PrivateRoute>
              <MyCourses userData={userData} />
            </PrivateRoute>
          } />
          <Route path="/my-courses-manager" element={
            <PrivateRoute>
              <MyCoursesManager userData={userData} />
            </PrivateRoute>
          } />
          <Route path="/componentsubmissions" element={
            <PrivateRoute>
              <ComponentSubmissions userData={userData} />
            </PrivateRoute>
          } />
          <Route path="/users-csv-import" element={
            <PrivateRoute>
              <CSVImport userData={userData} />
            </PrivateRoute>
          } />
          <Route path="/community" element={
            <PrivateRoute>
              <Community userData={userData} />
            </PrivateRoute>
          } />
          <Route path="/analytics" element={
            <PrivateRoute>
              <Analytics userData={userData} />
            </PrivateRoute>
          } />
          <Route path="/courses-previewer" element={
            <PrivateRoute>
              <AdminCourses userData={userData} />
            </PrivateRoute>
          } />
          <Route path="/all-hours" element={
            <PrivateRoute>
              <StudentHoursOverview userData={userData} />
            </PrivateRoute>
          } />
          <Route path="/email-test" element={
            <PrivateRoute>
              <EmailTest userData={userData} />
            </PrivateRoute>
          } />
          <Route path="/email-templator" element={
            <PrivateRoute>
              <EmailTemplateGenerator userData={userData} />
            </PrivateRoute>
          } />
          <Route path="/test-teamup-event" element={<TestTeamUpEvent />} />
          <Route path="/teamup-calendar-manager" element={
            <PrivateRoute>
              <TeamUpCalendarManager userData={userData} />
            </PrivateRoute>
          } />
          <Route path="/assignment" element={<PrivateRoute><AssignmentViewPage /></PrivateRoute>} />
          <Route path="/open-fe-event-survey" element={<OpenFEEventSurvey />} />
          <Route path="/action" element={<ActionLinkPage />} />
          <Route path="/open-registration-matcher" element={
            <PrivateRoute>
              <OpenRegistrationMatcher userData={userData} />
            </PrivateRoute>
          } />
          <Route path="/sms-notifications" element={<SMSNotifications userData={userData} />} />
          <Route path="/taxonomy" element={<Taxonomy userData={userData} />} />
          <Route path="/terms" element={<TermsOfService userData={userData} />} />
          <Route path="/privacy" element={<PrivacyPolicy userData={userData} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
        </Router>
  );
}

const styles = {
  accessDenied: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 'calc(100vh - 64px)',
    padding: '2rem',
    textAlign: 'center',
    backgroundColor: '#f8f9fa'
  },
  accessDeniedTitle: {
    color: '#1D4E89',
    fontSize: '2.5rem',
    marginBottom: '1rem'
  },
  accessDeniedText: {
    color: '#666',
    fontSize: '1.2rem'
  }
};

export default App; 